(async () => {
    const { Core } = await importModule('Core');
    const { Routes, parseHTML } = await importModule('Utils');
    
    const CART_R_SECTION = 'r_cart';
    const NOTIFICATION_SECTION = 'r_cart-notification';
    const ATTR_IS_CART_PAGE = 'cart-page';
    const SECTION_DRAWER = 'g_cart-drawer';

    customElements.define('cart-provider', class extends Core {
        subscriptions = {
            'cart:add-feature': '_addFeature',
            'cart:add': '_add',
            'cart:change': '_update'
        }

        features = {};

        render() {
            this.subscribe('cart:add-feature', { global: true });
            this.subscribe('cart:add', { global: true });
            this.subscribe('cart:change', { global: true });
        }

        _addFeature({ feature }) {
            this.features[feature] = true;
        }
        
        async _add({ items }) {
            try {
                const resBody = {items};
                if (this.features['notification']) {
                    resBody.sections = [NOTIFICATION_SECTION];
                    resBody.attributes = {
                        notification_items_variant_ids: items.map(({id}) => id).join(',')
                    };
                }
                const res = await this._requestCart('cartAdd', resBody);

                this._cleanCartAttributes();
                
                
                const cartData = await res.json();
                if(!res.ok) {
                    throw new Error(JSON.stringify({ message: cartData.message, description: cartData.description }))
                } else {
                    if (this.redirectToCart) {
                        window.location.pathname = Routes('cart');
                        return;
                    }
                    this._publishUpdates(cartData);
                }
            } catch (e) {
                console.error(e);
                const { message = '', description = '' } = JSON.parse(e.message);
                if (message || description)  {
                    this.publish(
                        'toast-notification:open', 
                        {   
                            title: message, 
                            message: description, 
                            type: 'error' 
                        }
                    )
                }
                this.publish('cart:error');
            }  
        }

        _cleanCartAttributes() {
            if(this.features['notification']) {
                this._requestCart('cartUpdate', { attributes: { notification_items_variant_ids: null }});
            }
        }

        async _update({ key, quantity, selling_plan }) {
            try {
                const sections = [CART_R_SECTION];
                if (this.features['notification']) {
                    sections.push(NOTIFICATION_SECTION);
                }
                const res = await this._requestCart('cartChange', {
                        id: key, quantity, selling_plan,
                        sections,
                        sections_url: Routes('cart')
                    }
                );

                const cartData = await res.json();
                if (!res.ok) {
                    throw new Error(cartData.message || 'Cart update failed');
                }
                this._publishUpdates(cartData);
            } catch (e) {
                console.error('Cart update error:', e);
                this.publish('cart:error');
                this.publish('cart:update', {}); // Clear loading states
            }
        }

        async _publishUpdates(cartData) {
            const eventData = {};  

            if (this.features['cart-total'] || this.features['counter'] || this.features['drawer']) 
                eventData.meta = await this._getCartMeta();

            if (this.features['drawer'])
                eventData.drawer = { 
                    section: await this._getDrawerSection(),
                    open: !this.features['notification']
                }

            if (this.features['notification'])
                eventData.notification = { 
                    section: cartData.sections[NOTIFICATION_SECTION],
                    recommendationProductId: cartData.items[0]?.product_id 
                }
                
            if (this.features['cart']) {
                const $cartDoc = parseHTML(cartData.sections[CART_R_SECTION]);
                eventData.cart = { 
                    getCount: () => $cartDoc.querySelector('[data-cart-counter]').innerText,
                    getItems: () => $cartDoc.querySelector('[data-cart-items]'),
                    getSubtotalBlock: () => $cartDoc.querySelector('[data-cart-subtotal-block]'),
                    getFreeShippingBlock: () => $cartDoc.querySelector('[data-cart-free-shipping-block]')
                }
            }

            this.publish('cart:update', eventData)
        }
        
        async _getCartMeta() {
            const res = await fetch(`${Routes('cart')}?view=meta`);
            const { count, ...data } = await res.json();
            return { count: Number(count), ...data };
        }

        async _getDrawerSection() {
            const res = await fetch(`${Routes('root')}?section_id=${SECTION_DRAWER}`);
            return await res.text();
        }

        _hasFileProperty(body) {
            return Array.isArray(body?.items) && body.items.some(
                item => Object.values(item?.properties || {}).some(v => v instanceof File)
            );
        }

        // Uploaded files reach /cart/add.js only through a multipart body — JSON
        // cannot carry them. Same payload shape, expressed as items[N] brackets;
        // Shopify stores each file and returns its CDN url as the property value.
        _toFormData({ items = [], sections, attributes }) {
            const form = new FormData();
            items.forEach((item, i) => {
                form.append(`items[${i}][id]`, item.id);
                form.append(`items[${i}][quantity]`, item.quantity ?? 1);
                Object.entries(item.properties || {}).forEach(([key, value]) => {
                    form.append(`items[${i}][properties][${key}]`, value);
                });
            });
            if (sections) form.append('sections', [].concat(sections).join(','));
            Object.entries(attributes || {}).forEach(([key, value]) => {
                form.append(`attributes[${key}]`, value);
            });
            return form;
        }

        _requestCart(route, body) {
            const asForm = this._hasFileProperty(body);
            return fetch(`${Routes(route)}.js`, {
                method: 'POST',
                // Content-Type is omitted for multipart so the browser adds the boundary.
                headers: asForm
                    ? { 'Accept': 'application/json' }
                    : { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: asForm ? this._toFormData(body) : JSON.stringify(body)
            })
        }

        get isCartPage() {
            return this.hasAttribute(ATTR_IS_CART_PAGE)
        }

        get redirectToCart() {
            return !this.isCartPage && !this.features['notification'] && !this.features['drawer'];
        }
    });

})();